import {
    Badge,
    Row,
    Col,
    Button,
    Divider,
    Image,
    Modal,
    Popover,
    Segmented,
    Space,
    Table,
    Tag,
    Tooltip,
    Typography
} from 'antd';
import React, { useContext, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axiosApiInstance from '../../../api/axiosClient';
import ProDescriptions from '@ant-design/pro-descriptions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCompactDisc, faMusic, faB } from '@fortawesome/free-solid-svg-icons';
import { faSpotify, faYoutube } from '@fortawesome/free-brands-svg-icons';
import ProCard from '@ant-design/pro-card';
import { ColumnsType } from 'antd/es/table';
import { atom, useAtom } from 'jotai';
import { DownloadOutlined, FileOutlined, StopOutlined , EyeOutlined} from '@ant-design/icons';
import XMLViewer from 'react-xml-viewer';
import dayjs from 'dayjs';
import { DDEXPageContext } from './DDEXMainPage';

interface AlbumCoverProps {
    upc?: string;
    filename?: string;
    id?: string | number;
}


export const AlbumCover = ({upc, filename, id}: AlbumCoverProps) => {
    // const [imageUrl, setImageUrl] = useState('blank');
    const { data: imageUrl } = useQuery({
        refetchInterval: 3 * 60 * 1000,
        staleTime: 3 * 60 * 1000,
        queryKey: ['album_cover', id, filename],
        queryFn: () => {
            if (!filename || !id) return 'blank';

            return axiosApiInstance
                .post<{ presignedUrl: string }>(
                    '/api/products/releases/generate-presigned-url-get',
                    {
                        fileName: filename,
                        folderName: id
                    }
                )
                .then(({ data }) => data.presignedUrl)
                .catch(() => 'blank');
        }
    });

    if (!filename || !id) {
        return null;
    }

    return (
        <div
            style={{
                width: '100%',
                maxWidth: 220,
                aspectRatio: '1',
                borderRadius: 12,
                overflow: 'hidden',
                background: '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid #f0f0f0'
            }}
        >
            <Image
                src={imageUrl && imageUrl !== 'blank' ? imageUrl : undefined}
                alt={upc || 'Album cover'}
                preview={false}
                fallback='https://placehold.co/400x400/ffffff/8c8c8c?text=No+Cover'
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
        </div>
    );
};


const ICON_WIDTH = 40;

const ReleaseTypeTagMap: Record<string, React.ReactNode> = {
    ALBUM: <Tag color='#722ed1' style={{ borderRadius: 8, padding: '2px 10px', fontWeight: 600 }}>💿 Album</Tag>,
    SINGLE: <Tag color='#52c41a' style={{ borderRadius: 8, padding: '2px 10px', fontWeight: 600 }}>🎵 Single</Tag>,
    EP: <Tag color='#13c2c2' style={{ borderRadius: 8, padding: '2px 10px', fontWeight: 600 }}>🎧 EP</Tag>
};

const UploadStatusesBadgeMap: Record<string, React.ReactNode> = {
    uploaded: <Badge status='success' text='Release' />,
    modified: <Badge color='blue' text='Update' />,
    takedown: <Badge status='error' text='Takedown' />
};

const UploadStatusesTagMap: Record<string, React.ReactNode> = {
    uploaded: <Tag color='success' style={{ borderRadius: 12, padding: '1px 8px' }}>Release</Tag>,
    modified: <Tag color='blue' style={{ borderRadius: 12, padding: '1px 8px' }}>Update</Tag>,
    takedown: <Tag color='error' style={{ borderRadius: 12, padding: '1px 8px' }}>Takedown</Tag>
};

const formatDate = (dateString?: string): string => {
    if (!dateString) return '-';
    const parsed = dayjs(dateString);
    return parsed.isValid() ? parsed.format('DD/MM/YYYY') : dateString;
};

const formatYear = (dateString?: string): string =>{
    if (!dateString) return '—';
    const parsed = dayjs(dateString);
    return parsed.isValid() ? parsed.format('YYYY') : dateString;
}

const selectedPlatform = atom('youtube');

//const formatDate = (dateString: string): string => dayjs(dateString).format('DD-MM-YYYY HH:mm:ss');//

const formatGenre = ({ genre, sub_genre }: API.MusicDistribution.MusicRelease): string => {
    var str = genre || '';
    if (sub_genre) {
        str = `${str} (${sub_genre})`;
    }
    return str || '—';
};

const renderArtists = (value: API.MusicDistribution.Artist[] | null | undefined): string => {
    if (!value || !Array.isArray(value)) {
        return '-';
    }
    return value.map((a) => a?.key_name).filter(Boolean).join(', ') || '-';
};

const trackTableColumns: ColumnsType<API.MusicDistribution.ArtTrack> = [
    {
        key: 'vol_no',
        dataIndex: 'volume_number',
        width: 50,
        align: 'center',
        title: (
            <Tooltip title='Volume Number'>
                <FontAwesomeIcon icon={faCompactDisc} />
            </Tooltip>
        )
    },
    {
        key: 'track_no',
        dataIndex: 'track_number',
        width: 50,
        align: 'center',
        title: (
            <Tooltip title='Track Number'>
                <FontAwesomeIcon icon={faMusic} />
            </Tooltip>
        )
    },
    {
        key: 'isrc',
        dataIndex: 'isrc',
        width: 140,
        title: 'ISRC',
        render: (value) => (
            <Typography.Text code style={{ fontSize: 12 }}>
                {value || '—'}
            </Typography.Text>   
        )
    },
    {
        key: 'title',
        dataIndex: 'title',
        width: 180,
        ellipsis: true,
        title: 'Title'
    },
    {
        key: 'main_artists',
        dataIndex: 'main_artists',
        width: 140,
        ellipsis: true,
        title: 'Main Artist(s)',
        render: renderArtists
    },
    {
        key: 'featuring_artists',
        dataIndex: 'featuring_artists',
        width: 120,
        ellipsis: true,
        title: 'Featuring',
        render: renderArtists
    },
    {
        key: 'authors',
        dataIndex: 'authors',
        width: 120,
        ellipsis: true,
        title: 'Author(s)',
        render: renderArtists
    },
    {
        key: 'composers',
        dataIndex: 'composers',
        width: 120,
        ellipsis: true,
        title: 'Composer(s)',
        render: renderArtists
    }
];

const uploadHistoryColumns: ColumnsType<API.MusicDistribution.UploadStatusLogs> = [
    {
        key: 'created_at',
        dataIndex: 'created_at',
        title: 'Timestamp',
        render: (value) => formatDate(value)
    },
    {
        key: 'status',
        dataIndex: 'status',
        title: 'Action',
        render: (value) => UploadStatusesBadgeMap[value] || value
    },
    {
        key: 'xml',
        dataIndex: 'message',
        title: '',
        render: (value, record) => (
            <Popover
                trigger='click'
                placement='left'
                content={
                    <ProCard
                        style={{
                            width: 1000,
                            height: 600,
                            overflowX: 'scroll',
                            overflowY: 'scroll',
                            padding: 15
                        }}
                    >
                        <ProCard
                            title={`${record.upc_ean}`}
                            subTitle={
                                <Space>
                                    {formatDate(record.created_at)} {UploadStatusesTagMap[record.status]}
                                </Space>
                            }
                            extra={
                                <Tooltip title='Download XML'>
                                    <Button
                                        type='link'
                                        icon={<DownloadOutlined />}
                                        onClick={() => {
                                            const url = window.URL.createObjectURL(
                                                new Blob([record.message])
                                            );
                                            const link = document.createElement('a');
                                            link.href = url;
                                            link.setAttribute(
                                                'download',
                                                `${record.upc_ean}_${record.created_at}_${record.status}.xml`
                                            );
                                            document.body.appendChild(link);
                                            link.click();
                                        }}
                                    />
                                </Tooltip>
                            }
                            bordered
                            headerBordered
                        >
                            <XMLViewer xml={value as string} collapsible />
                        </ProCard>
                    </ProCard>
                }
            >
                <Tooltip title='View Metadata'>
                    <Button icon={<FileOutlined />} type='link' />
                </Tooltip>
            </Popover>
        )
    }
];

const UploadPlatformsSegmented = () => {
    const [platform, setPlatform] = useAtom(selectedPlatform);
    return (
        <Segmented
            value={platform}
            options={[
                {
                    value: 'youtube',
                    icon: <FontAwesomeIcon icon={faYoutube} color='red' width={ICON_WIDTH} />,
                    title: 'youtube'
                },
                {
                    value: 'spotify',
                    icon: <FontAwesomeIcon icon={faSpotify} color='#1DB954' width={ICON_WIDTH} />
                },
                {
                    value: 'believe',
                    icon: <FontAwesomeIcon icon={faB} color='#000035' width={ICON_WIDTH} />
                }
            ]}
            onChange={(value) => setPlatform(value as string)}
            onResize={() => {}}
            onResizeCapture={() => {}}
        />
    );
};

const ViewReleaseModal = () => {
    const { upc_ean } = useParams();
    const navigate = useNavigate();
    const { releasesActionRef } = useContext(DDEXPageContext);
    const [platform, setPlatform] = useAtom(selectedPlatform);

    useEffect(() => {
        return () => {
            setPlatform('youtube');
        };
    }, []);

    const { data } = useQuery({
        queryKey: [
            {
                path: ['/api/products/releases', upc_ean],
                target: upc_ean
            }
        ],
        queryFn: ({ queryKey }) =>
            axiosApiInstance
                .get<API.MusicDistribution.MusicRelease>(`/api/products/releases/${queryKey[0].target}`, {
                    params: {
                        with_label: true,
                        with_artists: true,
                        with_track_artists: true,
                        with_history: true,
                        //with_release_upload_status_logs: true
                    }
                })
                .then(({ data }) => data),
        suspense: true
    });
    //console.log("RELEASE DATA:", data);

    const { data: uploadLogsData } = useQuery({
        
        /*queryKey: [
            {
                path: ['/api/products/releases', upc_ean, 'records'],
                target: upc_ean
            }
        ],*/
        queryKey: ['release-records', data?.upc_ean || upc_ean],

        queryFn: () =>
            axiosApiInstance
                .get<API.MusicDistribution.ReleaseUploadStatusLog[]>(
                    `/api/products/releases/${data?.upc_ean || upc_ean}/records`
            
                )
                .then(({ data }) => data),
        enabled: !!(data?.upc_ean || upc_ean),
        suspense: false
    });
    const [, setSelectedPlatform] = useAtom(selectedPlatform);
    useEffect(() => {
        if (Array.isArray(uploadLogsData) && uploadLogsData.length > 0) {
            const logWithPlatform = uploadLogsData.find((log: any) => log?.provider);
            const platformName = (logWithPlatform as any)?.provider;

            if (platformName) {
                setSelectedPlatform(platformName);
            }
        }
    }, [uploadLogsData, setSelectedPlatform]);
        /*if (Array.isArray(uploadLogsData) && uploadLogsData.length > 0) {
            const firstPlatform = uploadLogsData.find((log: any) => log.platform)?.platform;

            if (firstPlatform) {
                setPlatform(firstPlatform.toLowerCase());
            }
        }
    }, [uploadLogsData, setPlatform]);
    if (!data) return null;*/

    /*const coverUrl = ((data as unknown as { cover_url?: string; image_url?: string; cover_art?: string })
        .cover_url || (data as unknown as { cover_url?: string; image_url?: string; cover_art?: string }).image_url || (data as unknown as { cover_url?: string; image_url?: string; cover_art?: string }).cover_art) as string | undefined;*/
    
    const coverUrl = (data as any)?.album_cover_filename
      ? `https://vibe.digitalminds.com/storage/app/public/covers/${(data as any).album_cover_filename}`: undefined;

    console.log("RELEASE DATA FROM BACKEND:", data);
    return (
    <Modal
        open
        title={<Typography.Title level={4} style={{ margin: 0 }}>{data.name || 'Release Details'}</Typography.Title>}
        width={1100}
        onCancel={() => navigate(-1)}
        destroyOnClose
        footer={null}
    >
        {/* Top Header Toolbar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <Space size="middle">
                <Typography.Text style={{ fontWeight: 500, color: '#6b7280' }}>UPC:</Typography.Text>
                <Typography.Text 
                    copyable 
                    style={{ 
                        backgroundColor: '#f0f5ff', 
                        color: '#1d39c4', 
                        padding: '4px 10px', 
                        borderRadius: 6, 
                        fontWeight: 600,
                        border: '1px solid #adc6ff'
                    }}
                >
                    {data.upc_ean || '—'}
                </Typography.Text>
                <span style={{ marginLeft: 16, color: '#6b7280', fontWeight: 500 }}>Release Type:</span>
                {ReleaseTypeTagMap[data.type] || '—'}
            </Space>

            <Button
                danger
                type='primary'
                ghost
                icon={<StopOutlined />}
                onClick={async () => {
                    await axiosApiInstance
                        .put(`/api/products/releases/${upc_ean}/unverify`)
                        .catch((reason) => console.error(reason));
                    releasesActionRef?.current?.reload();
                }}
            >
                Unverify
            </Button>
        </div>

        {/* Top Section: Cover Image (Left) & Metadata Grid (Right) */}
        <Row gutter={16} align="top" style={{ marginBottom: 16 }}>
            {/* ΑΡΙΣΤΕΡΑ: Εικόνα Εξωφύλλου */}
            <Col span={6}>
                <AlbumCover 
                    id={data?.id} 
                    filename={(data as any)?.album_cover_filename} 
                    upc={data?.upc_ean}
                />
            </Col>
        
        </Row>

        {/* ΕΔΩ ΒΑΖΕΙΣ ΤΟ DIVIDER */}
        <Divider style={{ margin: '16px 0' }} />

        <Row gutter={[16, 16]}>
        {/* Εδώ ξεκινάνε τα Tracks & Upload History */}

            {/* ΔΕΞΙΑ: Πληροφορίες σε 2 Στήλες */}
            <Col span={18}>
                <ProDescriptions column={2} size="small" bordered={false} labelStyle={{ fontWeight: 600, color: 'var(--text-strong)', width: '130px' }}>
                    <ProDescriptions.Item label="Digital Release">
                        {formatDate(data.digital_release_date)}
                    </ProDescriptions.Item>
                    <ProDescriptions.Item label="Genre">
                        {formatGenre(data)}
                    </ProDescriptions.Item>

                    <ProDescriptions.Item label="Original Release">
                        {formatDate(data.original_release_date)}
                    </ProDescriptions.Item>
                    <ProDescriptions.Item label="Main Artists">
                        {renderArtists(data.artists)}
                    </ProDescriptions.Item>

                    <ProDescriptions.Item label="Production Year">
                        {formatYear(data.production_year)}
                    </ProDescriptions.Item>
                    <ProDescriptions.Item label="Featured Artists">
                        {renderArtists(data.featuring_artists)}
                    </ProDescriptions.Item>

                    <ProDescriptions.Item label="℗ Line">
                        {data.p_line || data.label?.p_line || '—'}
                    </ProDescriptions.Item>
                    <ProDescriptions.Item label="Label">
                        {data.label?.name ? (
                            <Tag color="geekblue" style={{ borderRadius: 6, fontWeight: 500, padding: '2px 8px' }}>
                                🏷️ {data.label.name}
                            </Tag>
                        ) : '—'}
                    </ProDescriptions.Item>

                    <ProDescriptions.Item label="© Line">
                        {data.c_line || data.label?.c_line || '—'}
                    </ProDescriptions.Item>
                    <ProDescriptions.Item label="Asset Label">
                        {data.asset_label?.name ? (
                            <Tag color="magenta" style={{ borderRadius: 6, border: 'none', fontWeight: 500, padding: '2px 8px' }}>
                                🏢 {data.asset_label.name}
                            </Tag>
                        ) : '—'}
                    </ProDescriptions.Item>
                </ProDescriptions>
            </Col>
        </Row>

        {/* Bottom Section: Tables */}
        <ProCard colSpan={24} split='vertical' style={{ marginTop: 12, border: '1px solid #f0f0f0', borderRadius: 8 }}>
            <ProCard colSpan={14} title='Tracks' headerBordered>
                <Table
                    columns={trackTableColumns}
                    dataSource={data.art_tracks || []}
                    pagination={false}
                    scroll={{ y: 280, x: 'max-content' }}
                    size='small'
                    rowKey="id"
                />
            </ProCard>
            <ProCard colSpan={10} title='Upload History' extra={<UploadPlatformsSegmented />} headerBordered>
                <Table
                    columns={uploadHistoryColumns}
                    dataSource={(uploadLogsData || []).filter((log: any) => {
                        const itemPlatform = (log.platform || log.provider || '').toString().toLowerCase();
                        const currentPlatform = (platform || '').toString().toLowerCase();
                        return itemPlatform === currentPlatform;
                    })}
                    
                    pagination={false}
                    scroll={{ y: 280 }}
                    size='small'
                    rowKey="id"
                />
            </ProCard>
        </ProCard>
    </Modal>
    );
};

export default ViewReleaseModal;